import { useEffect, useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import moment from 'moment';

import { Services } from 'services';
import { useAppSelector } from 'modules/state-mgmt/store';
import { prettifyErrorResponse } from 'utils';
import {
  IDrugTestPanel,
  IDrugTestingType,
  IFacilityAddress,
  IFacilityDetail,
  IWorkingSchedule,
} from 'models/drugTesting';

export interface ISelectOption {
  value: string;
  label: string;
}

export interface IFacilityOption extends ISelectOption {
  // State name used by the grouped Select dropdown (state header → its facilities).
  group: string;
}

export interface IFacilityInfo {
  name: string;
  address: string;
  hoursOfOperation: string;
}

const FACILITIES_PAGE_SIZE = 30;

// .NET System.DayOfWeek ordering (0 = Sunday).
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const buildFacilityAddress = (address: IFacilityAddress | null): string => {
  if (!address) return '';
  return [address.line1, address.line2, address.city, address.state, address.zipCode]
    .filter(part => part && String(part).trim())
    .join(', ');
};

// startTime/endTime arrive as "HH:mm" or "HH:mm:ss" — render as 12-hour with AM/PM.
const formatTime = (time?: string): string => {
  if (!time) return '';
  const parsed = moment(time, ['HH:mm:ss', 'HH:mm'], true);
  return parsed.isValid() ? parsed.format('h:mm A') : '';
};

const buildHoursOfOperation = (schedules: IWorkingSchedule[] = []): string =>
  [...schedules]
    .sort((a, b) => a.dayOfWeek - b.dayOfWeek)
    .map(schedule => `${DAY_NAMES[schedule.dayOfWeek] ?? ''}: ${formatTime(schedule.startTime)} - ${formatTime(schedule.endTime)}`)
    .join('\n');

// invoiceToCompanyId is the company the worker joined the project with first — pick the one
// with the oldest joinedAt. Companies missing a joinedAt sort last so a dated one always wins.
const getOldestJoinedCompanyId = (companies: any[] = []): string => {
  const validCompanies = companies.filter(item => item?.company?.id);
  if (!validCompanies.length) return '';
  const oldest = validCompanies.reduce((acc, item) => {
    const accTime = acc.joinedAt ? new Date(acc.joinedAt).getTime() : Infinity;
    const itemTime = item.joinedAt ? new Date(item.joinedAt).getTime() : Infinity;
    return itemTime < accTime ? item : acc;
  });
  return oldest.company.id || '';
};

export const useBookAppointmentOptions = (
  selectedProjectId?: string,
  selectedFacilityId?: string,
  enabled: boolean = true,
) => {
  const { workerId, languageCode, isWorker } = useAppSelector(state => ({
    // Direct worker dashboard uses user.workerId; onboarding.id is set when an admin acts on a worker.
    workerId: state.onboarding.id || state.user?.workerId,
    languageCode: state.app?.languageCode,
    isWorker: state.user?.isWorker,
  }));

  const [projects, setProjects] = useState<ISelectOption[]>([]);
  // projectId → invoiceToCompanyId (the company with the oldest joinedAt for that project).
  const [projectCompanyMap, setProjectCompanyMap] = useState<Record<string, string>>({});
  const [facilities, setFacilities] = useState<IFacilityOption[]>([]);
  const [panels, setPanels] = useState<ISelectOption[]>([]);
  const [types, setTypes] = useState<ISelectOption[]>([]);
  const [facilityInfo, setFacilityInfo] = useState<IFacilityInfo | null>(null);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [isLoadingFacilities, setIsLoadingFacilities] = useState(false);
  const [isLoadingPanels, setIsLoadingPanels] = useState(false);
  const [isLoadingTypes, setIsLoadingTypes] = useState(false);
  const [isLoadingFacilityInfo, setIsLoadingFacilityInfo] = useState(false);

  // Load the worker's current projects to populate the Project dropdown.
  useEffect(() => {
    if (!enabled || !workerId) return;

    setIsLoadingProjects(true);
    Services.getWorkerLiteProjects(workerId, 1, undefined, 1 /* status 1 = current/active */, undefined, languageCode || 'en', true)
      .then(response => {
        const items = response.data?.items || [];
        // Only projects with drug testing active are eligible for booking appointments.
        const validItems = items.filter((item: any) => item.project?.id && item.isDrugTestingActive);
        const projectOptions: ISelectOption[] = validItems.map((item: any) => ({
          value: item.project.id,
          label: item.project.name || '',
        }));
        setProjects(projectOptions);
        // Capture the oldest-joined company per project for invoiceToCompanyId.
        const companyMap: Record<string, string> = {};
        validItems.forEach((item: any) => {
          companyMap[item.project.id] = getOldestJoinedCompanyId(item.companies);
        });
        setProjectCompanyMap(companyMap);
      })
      .catch(error => {
        enqueueSnackbar(prettifyErrorResponse(error), { variant: 'error' });
      })
      .finally(() => {
        setIsLoadingProjects(false);
      });
  }, [enabled, workerId, languageCode]);

  // Load testing facilities (grouped by state) for the selected project.
  useEffect(() => {
    if (!selectedProjectId) {
      setFacilities([]);
      return;
    }

    setIsLoadingFacilities(true);
    Services.getDrugTestingFacilitiesGroupedByState(
      selectedProjectId,
      { pageNumber: 1, pageSize: FACILITIES_PAGE_SIZE },
      languageCode || 'en',
    )
      .then(response => {
        const groups = response.data?.items || [];
        const facilityOptions: IFacilityOption[] = groups.flatMap((group: any) =>
          (group.facilities || []).map((facility: any) => ({
            value: facility.id,
            label: facility.name || '',
            group: group.stateName || '',
          })),
        );
        setFacilities(facilityOptions);
      })
      .catch(error => {
        enqueueSnackbar(prettifyErrorResponse(error), { variant: 'error' });
        setFacilities([]);
      })
      .finally(() => {
        setIsLoadingFacilities(false);
      });
  }, [selectedProjectId, languageCode]);

  // Load drug-testing types for the selected project.
  useEffect(() => {
    if (!selectedProjectId) {
      setTypes([]);
      return;
    }

    setIsLoadingTypes(true);
    Services.getProjectDrugTestingTypes(selectedProjectId, languageCode || 'en')
      .then(response => {
        const typeList: IDrugTestingType[] = response.data || [];
        // Only Standard and Renewal are bookable from this form.
        const allowedTypes = ['standard', 'renewal'];
        setTypes(
          typeList
            .filter(type => allowedTypes.includes((type.name || '').trim().toLowerCase()))
            .map(type => ({ value: type.id, label: type.name || '' })),
        );
      })
      .catch(error => {
        enqueueSnackbar(prettifyErrorResponse(error), { variant: 'error' });
        setTypes([]);
      })
      .finally(() => {
        setIsLoadingTypes(false);
      });
  }, [selectedProjectId, languageCode]);

  // Load testing panels + facility details once a facility is selected.
  useEffect(() => {
    if (!selectedFacilityId || !selectedProjectId) {
      setPanels([]);
      setFacilityInfo(null);
      return;
    }

    setIsLoadingPanels(true);
    Services.getFacilityTestPanelsByFacilityIds(
      { facilityIds: [selectedFacilityId], projectId: selectedProjectId },
      languageCode || 'en',
    )
      .then(response => {
        const panelList: IDrugTestPanel[] = response.data || [];
        setPanels(panelList.map(panel => ({ value: panel.id, label: panel.name || '' })));
      })
      .catch(error => {
        enqueueSnackbar(prettifyErrorResponse(error), { variant: 'error' });
        setPanels([]);
      })
      .finally(() => {
        setIsLoadingPanels(false);
      });

    setIsLoadingFacilityInfo(true);
    Services.getDrugTestingFacility(selectedFacilityId, languageCode || 'en', isWorker || false)
      .then(response => {
        const facility: IFacilityDetail = response.data;
        setFacilityInfo({
          name: facility?.name || '',
          address: buildFacilityAddress(facility?.facilityAddress || null),
          hoursOfOperation: buildHoursOfOperation(facility?.workingSchedules || []),
        });
      })
      .catch(error => {
        enqueueSnackbar(prettifyErrorResponse(error), { variant: 'error' });
        setFacilityInfo(null);
      })
      .finally(() => {
        setIsLoadingFacilityInfo(false);
      });
  }, [selectedFacilityId, selectedProjectId, languageCode, isWorker]);

  // Oldest-joined company of the selected project — used as invoiceToCompanyId.
  const invoiceToCompanyId = selectedProjectId ? projectCompanyMap[selectedProjectId] || '' : '';

  return {
    projects,
    facilities,
    panels,
    types,
    facilityInfo,
    invoiceToCompanyId,
    isLoadingProjects,
    isLoadingFacilities,
    isLoadingPanels,
    isLoadingTypes,
    isLoadingFacilityInfo,
  };
};
